import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { refreshBugAnalysisRepos } from '../../services/apiService';
import { isAdmin } from '../../services/authService';

/**
 * Admin-only: clone or pull all Repository records into BUG_ANALYSIS_REPOS_DIR for bug code analysis.
 */
const BugAnalysisReposRefreshPanel = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);

  const handleRefresh = async () => {
    try {
      setLoading(true);
      setError(null);
      setSummary(null);
      const res = await refreshBugAnalysisRepos();
      if (res.success && res.data) {
        setSummary(res.data);
      } else {
        setError(res.message || 'Refresh failed');
      }
    } catch (err) {
      setError(err.message || 'Refresh failed');
    } finally {
      setLoading(false);
    }
  };

  if (!isAdmin()) {
    return null;
  }

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Bug analysis — local repositories
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Clones or updates Git repositories from the Repositories collection into the directory set by{' '}
        <code>BUG_ANALYSIS_REPOS_DIR</code> in the server environment (default: <code>repos</code> under the app
        root). GitHub organization for clone URLs comes from <code>BUG_ANALYSIS_GITHUB_ORG</code> or{' '}
        <code>GITHUB_ORG</code> (default <code>tr</code>), not from per-repo owner fields in the database. Uses the
        GitHub PAT from <code>GHE_TOKEN</code> / <code>GITHUB_TOKEN</code> or Admin Access Tokens. Existing folders:{' '}
        <code>git fetch</code>, <code>git reset --hard</code>, <code>git pull</code>. Missing folders:{' '}
        <code>git clone</code>.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {summary && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Target directory: <code>{summary.reposDir}</code>
            {' — '}
            {summary.results?.filter((r) => r.status === 'ok').length || 0} ok,{' '}
            {summary.results?.filter((r) => r.status === 'error').length || 0} errors
          </Alert>
          {summary.results && summary.results.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Repository</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Message</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {summary.results.map((row, i) => (
                    <TableRow key={`${row.githubRepoName}-${i}`}>
                      <TableCell>{row.fullRepoName || row.githubRepoName}</TableCell>
                      <TableCell>{row.action}</TableCell>
                      <TableCell>{row.status}</TableCell>
                      <TableCell sx={{ maxWidth: 360, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {row.message || '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading}
        >
          {loading ? 'Refreshing…' : 'Refresh Repos'}
        </Button>
      </Box>
    </Paper>
  );
};

export default BugAnalysisReposRefreshPanel;
